import React, { createContext, useState } from "react";
import { Box, Container, useColorMode } from "@chakra-ui/react";
import { NavbarWithCallToAction } from "./chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "./chakra/FooterWithFourColumns/FooterWithFourColumns";
import ModuleNavLauncher from "./ModuleNavLauncher";
import { ModuleConfig } from "../types/ModuleConfig";
import { getColor } from "../brandConfig";
import { useAuth } from "../contexts/AuthContext";

/**
 * Runtime chrome control for pages inside ModulePageLayout. Lets a page hide
 * the breadcrumb bar dynamically (e.g. the calendar's Fit mode reclaims that
 * strip for the grid). Static hiding stays on the route's `noBreadcrumb`.
 */
export const LayoutChromeContext = createContext<{
  setBreadcrumbHidden: (hidden: boolean) => void;
}>({ setBreadcrumbHidden: () => {} });

interface ModulePageLayoutProps {
  moduleConfig: ModuleConfig;
  children: React.ReactNode;
  /** Container maxW - defaults to "container.xl" */
  maxW?: string;
  /** Container py - defaults to 8 */
  py?: number;
  /** Skip Container wrapper entirely (for pages that manage their own layout) */
  noContainer?: boolean;
  /** Skip breadcrumb */
  noBreadcrumb?: boolean;
  /** Skip footer (full-bleed canvas pages) */
  noFooter?: boolean;
  /** Pin the frame to exactly 100dvh + overflow:hidden — the page owns its own
      internal scroll regions (e.g. knowledgeexplorer's 3 panes). */
  fillViewport?: boolean;
}

/**
 * ModulePageLayout — Shared layout wrapper for module pages.
 *
 * Provides consistent page scaffolding:
 *   1. NavbarWithCallToAction (top)
 *   2. ModuleNavLauncher with resolved permissions (floating, draggable)
 *   3. Container wrapping page content (configurable maxW)
 *   4. FooterWithFourColumns (bottom, pushed down by flex)
 *
 * Usage: Set `useLayout: true` on routes in moduleConfig.ts.
 * App.tsx wraps those routes with this component automatically,
 * passing the RESOLVED moduleConfig (with real permissions from backend).
 *
 * Page components should return ONLY their content — no Navbar, Footer,
 * Breadcrumb, Container, or outer Box wrappers.
 */
export const ModulePageLayout: React.FC<ModulePageLayoutProps> = ({
  moduleConfig,
  children,
  maxW = "container.xl",
  py = 8,
  noContainer = false,
  noBreadcrumb = false,
  noFooter = false,
  fillViewport = false,
}) => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const { isAuthenticated } = useAuth();
  const [breadcrumbHidden, setBreadcrumbHidden] = useState(false);

  return (
    <LayoutChromeContext.Provider value={{ setBreadcrumbHidden }}>
      <Box
        bg={bgMain}
        display="flex"
        flexDirection="column"
        {...(fillViewport ? { h: "100dvh", overflow: "hidden" } : { minHeight: "100vh" })}
      >
        <NavbarWithCallToAction />
        {/* Breadcrumb is authenticated module-navigation — hide it for logged-out guests
            following a public shared link (e.g. /bill/:id), or when the page asked for the
            space at runtime via LayoutChromeContext (e.g. calendar Fit mode). */}
        {/* The nav launcher replaces the retired sticky breadcrumb bar. The two
            flags keep their old names on purpose — every existing caller and every
            `noBreadcrumb: true` route works unchanged. */}
        {!noBreadcrumb && !breadcrumbHidden && isAuthenticated && <ModuleNavLauncher moduleConfig={moduleConfig} />}
        {noContainer ? (
          <Box
            flex="1"
            {...(fillViewport ? { minH: 0, overflow: "hidden", display: "flex", flexDirection: "column" } : {})}
          >
            {children}
          </Box>
        ) : (
          <Container maxW={maxW} py={py} flex="1">
            {children}
          </Container>
        )}
        {!noFooter && <FooterWithFourColumns />}
      </Box>
    </LayoutChromeContext.Provider>
  );
};
